import { toUtcMidnightIso } from "@/components/calendars/allDayDates.ts";
import { parseUnitsExhaustedError } from "@/components/billing/UnitsExhaustedDialog.tsx";
import { type Calendar, updateEvent } from "@/components/calendars/api.ts";
import { canDragEvent, isReminderBlob } from "@/components/calendars/canEditEvent.ts";
import { sourceIdOf } from "@/components/calendars/eventTargeting.ts";
import { useRecurringScopePromise } from "@/components/calendars/useRecurringScopePromise.tsx";
import { useRescheduleConfirmPromise } from "@/components/calendars/useRescheduleConfirmPromise.tsx";
import {
  addPendingBufferMoves,
  removePendingBufferMoves,
} from "@/components/calendars/pendingBufferMoves.ts";
import { pendingThreadMessagesAtom } from "@/atoms/messagesAtoms.ts";
import { eventDetailPath } from "@/components/calendars/calendar-url.ts";
import { setEventProposal } from "@/components/calendars/pendingEventProposal.ts";
import { useCanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import { buildOptimisticUserMessage } from "@/components/messages/hooks/useSendMessage.tsx";
import { getThreadPagePath } from "@/components/threads/util.ts";
import { toUrnString } from "@/types/urn.ts";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { createNewTopicThread, updateThreadOptimistically } from "@/hooks/useThreads.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import type {
  CalendarEvent,
  CalendarEventParticipant,
  RecurringEditScope,
} from "@/types/events.ts";
import type { OkthxSourceBlob } from "@/types/calendarEvent.ts";
import type { NewMessageReq, NewMessageResp } from "@/types/threadEvents.ts";
import type { EventApi, EventDropArg } from "@fullcalendar/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { isAxiosError } from "axios";
import { addDays, format } from "date-fns";
import { useSetAtom } from "jotai";
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { toast as sonnerToast } from "sonner";
import { useLocation } from "wouter";

/**
 * Typed view of the `extendedProps` payload that `useCalendarEvents` writes
 * into each FC event. Defined locally because FC types `extendedProps` as a
 * generic `Record<string, unknown>` and TypeScript's index-signature rule
 * forces bracket-access on every read otherwise.
 */
type CalendarEventExtendedProps = {
  calendarId?: string;
  htmlLink?: string;
  description?: string;
  isPast?: boolean;
  formattedRange?: string;
  isAllDay?: boolean;
  location?: string;
  status?: string;
  participants?: CalendarEventParticipant[];
  pendingSyncUpdate?: boolean;
  externalEventId?: string;
  recurringEventId?: string;
  isRecurring?: boolean;
  guestsCanModify?: boolean;
  bufferExternalEventIds?: { beforeId?: string; afterId?: string };
  okthx?: OkthxSourceBlob;
};

function getExtendedProps(eventApi: EventApi): CalendarEventExtendedProps {
  return eventApi.extendedProps as CalendarEventExtendedProps;
}

/** The external event ids of an event's travel-buffer holds, if any. */
function bufferIdsOf(eventApi: EventApi): string[] {
  const linkage = getExtendedProps(eventApi).bufferExternalEventIds;
  if (!linkage) return [];
  return [linkage.beforeId, linkage.afterId].filter((id): id is string => !!id);
}

/**
 * Shift the calendar's travel-buffer events (matched by external event id) by
 * `deltaMs`, updating them in-frame via FC `setDates`. Returns the new positions
 * so the caller can record them in the pendingBufferMoves overlay (which keeps
 * them put until the async backend cascade catches up). Used both for the
 * forward drag and the reverse (undo / failed-drag) direction.
 */
export function shiftBufferEvents(
  info: EventDropArg,
  bufferExternalEventIds: string[],
  deltaMs: number,
): { externalEventId: string; startMs: number; endMs: number }[] {
  if (bufferExternalEventIds.length === 0 || deltaMs === 0) return [];
  const ids = new Set(bufferExternalEventIds);
  const moves: { externalEventId: string; startMs: number; endMs: number }[] = [];
  for (const ev of info.view.calendar.getEvents()) {
    const ext = getExtendedProps(ev).externalEventId;
    if (!ext || !ids.has(ext) || !ev.start || !ev.end) continue;
    const start = new Date(ev.start.getTime() + deltaMs);
    const end = new Date(ev.end.getTime() + deltaMs);
    ev.setDates(start, end);
    moves.push({ externalEventId: ext, startMs: start.getTime(), endMs: end.getTime() });
  }
  return moves;
}

/**
 * Project FullCalendar's `EventApi` back into a CalendarEvent-shaped object
 * sufficient for the `canDragEvent` predicate. Both `canDragEvent` and
 * `canEditEventInSidebar` only inspect the fields we forward via
 * `useCalendarEvents`'s extendedProps mapping, so this projection is enough
 * — we don't need a full CalendarEvent.
 */
function fcEventToMinimalCalendarEvent(eventApi: EventApi): CalendarEvent {
  const ep = getExtendedProps(eventApi);
  return {
    calendarId: ep.calendarId ?? "",
    summary: eventApi.title,
    start: { dateTime: eventApi.start?.toISOString() ?? "", timeZone: "" },
    end: { dateTime: eventApi.end?.toISOString() ?? "", timeZone: "" },
    htmlLink: ep.htmlLink ?? "",
    formattedRange: ep.formattedRange ?? "",
    isPast: !!ep.isPast,
    externalEventId: ep.externalEventId,
    recurringEventId: ep.recurringEventId,
    isRecurring: ep.isRecurring,
    guestsCanModify: ep.guestsCanModify,
    participants: ep.participants,
    status: ep.status,
    pendingSyncUpdate: ep.pendingSyncUpdate,
    okthx: ep.okthx,
  };
}

/**
 * Whether the reschedule dialog should offer "Don't send an email to guests"
 * for this calendar — i.e. whether the provider can suppress organizer-update
 * notifications via its update API.
 *
 * - Google: yes — `sendUpdates: "none"` moves the event and emails no one.
 * - Microsoft Graph: no — `PATCH /events/{id}` always notifies attendees on an
 *   organizer update, with no knob to suppress it. (Outlook events aren't
 *   drag-reschedulable today anyway, so they never reach this dialog.)
 *
 * A missing/unknown `provider` (e.g. a response from a backend that predates
 * the field) is treated as unsupported so the affordance stays hidden rather
 * than silently failing.
 */
export function supportsSendWithoutEmail(calendar: Calendar | undefined): boolean {
  return calendar?.provider === "google";
}

/**
 * Snapshot the dragged event at drop time. FC's API exposes start/end as
 * Date objects in the browser TZ — exactly what we want to PATCH to the
 * provider (browser TZ wins per spec).
 */
export function snapshotEvent(eventApi: EventApi): {
  title: string;
  start: Date;
  end: Date;
  isAllDay: boolean;
  sourceEntId: string | null;
  calendarId: string | null;
  externalEventId: string | null;
  recurringEventId: string | null;
  isRecurring: boolean;
  participants: CalendarEvent["participants"];
  guestsCanModify: boolean;
  isReminder: boolean;
} {
  const start = eventApi.start ?? new Date();
  const isAllDay = eventApi.allDay;
  // FC's allDay end is exclusive (day-after) and may be null for single-day.
  // For timed events, end may also be null when the event has no explicit end.
  let end: Date;
  if (eventApi.end) {
    end = eventApi.end;
  } else if (isAllDay) {
    end = addDays(start, 1);
  } else {
    end = new Date(start.getTime() + 60 * 60 * 1000); // 1h fallback
  }
  const ep = getExtendedProps(eventApi);
  return {
    title: eventApi.title ?? "",
    start,
    end,
    isAllDay,
    // FC's `event.id` is a stable composite for recurring instances (see
    // fullCalendarEventId); the real source ent id travels in
    // `extendedProps.gcalId`. Resolve via sourceIdOf so a recurring drag PATCHes
    // the numeric ent id, not the composite (which 500s the bigint id lookup —
    // BIG-6305). Non-recurring/synthetic events fall back to `event.id`.
    sourceEntId: sourceIdOf(eventApi) || null,
    calendarId: ep.calendarId ?? null,
    externalEventId: ep.externalEventId ?? null,
    recurringEventId: ep.recurringEventId ?? null,
    isRecurring: !!ep.isRecurring,
    participants: ep.participants ?? [],
    guestsCanModify: !!ep.guestsCanModify,
    isReminder: isReminderBlob(ep.okthx),
  };
}

/**
 * Date/all-day fields for the events/update payload of a drag move (or its
 * undo). The returned `isAllDay` field must accompany any move that involves
 * an all-day event — dragging an all-day chip into the timed grid converts it
 * (and vice versa), and without the explicit flag the Google applier PATCHes
 * a `dateTime` without clearing the instance's all-day `date`, which
 * materializes a duplicate timed copy next to the surviving all-day one
 * (BIG-6304). All-day boundaries are sent as UTC midnight of the local
 * calendar day, matching the sidebar edit path. Exported for tests.
 * @param isAllDay - all-day state the move should end in
 * @param isAllDayRelevant - whether either side of the move is all-day; when
 *   false (timed→timed) the payload omits `isAllDay`, since no conversion is
 *   needed
 */
export function buildMoveDates(
  start: Date,
  end: Date,
  isAllDay: boolean,
  isAllDayRelevant: boolean,
  isReminder = false,
): { startDateTime: string; endDateTime: string; isAllDay: boolean | undefined } {
  // A reminder is a moment: `endDateMs === startDateMs` is the invariant every
  // reminder is stored with. The grid draws it over half an hour so it has a
  // block to lay out (`reminderChipEnd`), and that end is what arrives here on a
  // drop — so collapse it back, or a drag quietly turns the reminder into a
  // half-hour span. The chip renders start only, so nothing would show it.
  const effectiveEnd = isReminder ? start : end;
  return {
    startDateTime: isAllDay ? toUtcMidnightIso(start) : start.toISOString(),
    endDateTime: isAllDay ? toUtcMidnightIso(effectiveEnd) : effectiveEnd.toISOString(),
    isAllDay: isAllDayRelevant ? isAllDay : undefined,
  };
}

/**
 * Translate a server error from the events/update PATCH into a user-visible
 * toast string. The backend returns 4xx for permission / validation issues
 * and 5xx for transport errors.
 */
function errorMessageFor(err: unknown): string {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 403) return "Only the organizer can reschedule this event";
    if (status && status >= 500) return "Couldn't move event. Restored to original time.";
  }
  return "Couldn't move event";
}

export type UseEventDragOptions = {
  /** Master enable — when false, the returned callbacks no-op (FC won't
   *  initiate any drag). */
  enabled: boolean;
  /** Map of calendarId → Calendar metadata. Used for accessRole check.
   *  When undefined, drag is disabled (safer than guessing). */
  calendarsById: Map<string, Calendar> | undefined;
};

export type UseEventDragResult = {
  /** Spread these into the <FullCalendar /> props block to enable drag. */
  fcProps: {
    editable: boolean;
    eventStartEditable: boolean;
    eventDurationEditable: boolean;
    eventAllow: (
      dropInfo: { start: Date; end: Date; allDay: boolean },
      draggedEvent: EventApi | null,
    ) => boolean;
    eventDragStart: (info: { el: HTMLElement; event: EventApi }) => void;
    eventDragStop: (info: { el: HTMLElement; event: EventApi }) => void;
    eventDrop: (info: EventDropArg) => void;
  };
  /** JSX nodes to render somewhere in the tree (dialogs portaled via Radix). */
  dragElements: ReactNode;
};

const UNDO_TOAST_DURATION_MS = 8_000;

/**
 * Imperative controller that owns every piece of side-effecting DOM state
 * useEventDrag manipulates outside React: the ghost clone planted at the
 * source position, the global pointermove/mousemove listener that drives
 * the month-view day-cell highlight, the `body.calendar-dragging-event`
 * class that flips the document cursor, the day-cell tags themselves, and
 * the reference to the in-flight drag's source element (which the Esc
 * handler needs to dispatch a synthetic `pointercancel` against — FC's
 * interaction plugin does not expose a keyboard-cancellation API).
 *
 * Centralizing this is load-bearing: there are three teardown paths
 * (normal drag-stop, Esc, unmount) and historically each re-implemented
 * its own cleanup — meaning every new exit point was a fresh chance to
 * leak the body class / listeners / ghost. Now there are two methods —
 * `endDrag()` for the normal mouse-up flow (releases session-scoped state
 * but preserves the ghost until the drop dialog/toast terminally resolves)
 * and `dispose()` for the abandon flow (tears down everything, including
 * the ghost). All call sites delegate; the controller's internal state
 * (`ghostEl`, `moveHandler`, `activeEl`) is unreachable from the outside.
 */
type DragVisualController = {
  /** Begin a drag visual session: plants the ghost clone, attaches the
   *  pointermove tracker, sets the body class, records the source element. */
  start: (sourceEl: HTMLElement | null) => void;
  /** Normal mouse-up: release listener / body class / target tags. Keeps
   *  the ghost so it survives until a terminal drop branch resolves. */
  endDrag: () => void;
  /** Remove the ghost. Called at every terminal branch of `eventDrop`
   *  (success, failure, dialog dismissal, recurring scope cancel). */
  removeGhost: () => void;
  /** Full teardown — `endDrag()` + `removeGhost()`. Used by Esc and on
   *  unmount, where there's no normal mouse-up to drive the drop flow. */
  dispose: () => void;
  /** The source `el` recorded by `start`. Esc uses this to dispatch
   *  `pointercancel` and abort FC's pointer-driven drag state. */
  getActiveEl: () => HTMLElement | null;
};

function createDragVisualController(): DragVisualController {
  let ghostEl: HTMLElement | null = null;
  let moveHandler: ((e: PointerEvent | MouseEvent) => void) | null = null;
  let activeEl: HTMLElement | null = null;

  const releaseSessionState = () => {
    activeEl = null;
    document.body.classList.remove("calendar-dragging-event");
    if (moveHandler) {
      document.removeEventListener("pointermove", moveHandler as EventListener);
      document.removeEventListener("mousemove", moveHandler as EventListener);
      moveHandler = null;
    }
    for (const el of document.querySelectorAll(".calendar-drag-target-day")) {
      el.classList.remove("calendar-drag-target-day");
    }
  };

  const removeGhost = () => {
    if (ghostEl) {
      ghostEl.remove();
      ghostEl = null;
    }
  };

  return {
    start: (sourceEl) => {
      activeEl = sourceEl;
      document.body.classList.add("calendar-dragging-event");

      // Plant a static ghost at the original chip's location. Clone the
      // HARNESS (FC's positioning wrapper) so the clone inherits FC's CSS
      // context (ancestor selectors paint the chip correctly). Absolute-
      // position the clone inside the harness's parent using the original
      // harness's offset — pins the ghost to the original visual slot
      // regardless of what FC does to surrounding elements as the chip moves.
      if (sourceEl) {
        const harness = sourceEl.closest(
          ".fc-timegrid-event-harness, .fc-daygrid-event-harness",
        ) as HTMLElement | null;
        const parent = harness?.parentElement ?? null;
        if (harness && parent) {
          const harnessRect = harness.getBoundingClientRect();
          const parentRect = parent.getBoundingClientRect();
          const ghost = harness.cloneNode(true) as HTMLElement;
          ghost.classList.add("calendar-drag-ghost");
          // Don't intercept pointer events — the harness's child chip would
          // otherwise capture clicks meant for the drop target.
          ghost.style.pointerEvents = "none";
          ghost.style.position = "absolute";
          ghost.style.top = `${harnessRect.top - parentRect.top}px`;
          ghost.style.left = `${harnessRect.left - parentRect.left}px`;
          ghost.style.width = `${harnessRect.width}px`;
          ghost.style.height = `${harnessRect.height}px`;
          ghost.style.margin = "0";
          parent.appendChild(ghost);
          ghostEl = ghost;
        }
      }

      // Month view: track the day cell under the cursor and tag it with
      // `.calendar-drag-target-day` so CSS can render a target highlight.
      // FC doesn't expose a per-pointer-move callback, so listen globally
      // and re-tag the cell each tick. Cheap — one `elementsFromPoint` +
      // a class toggle per move.
      const handler = (e: PointerEvent | MouseEvent) => {
        const stack = document.elementsFromPoint(e.clientX, e.clientY);
        let hitCell: HTMLElement | null = null;
        for (const node of stack) {
          const cell = (node as Element).closest(".fc-daygrid-day") as HTMLElement | null;
          if (cell) {
            hitCell = cell;
            break;
          }
        }
        const date = hitCell?.dataset["date"] ?? null;
        const dateMatches = date
          ? document.querySelectorAll<HTMLElement>(`.fc-daygrid-day[data-date="${date}"]`)
          : ([] as unknown as NodeListOf<HTMLElement>);
        const toTag = new Set<HTMLElement>(dateMatches);
        for (const prev of document.querySelectorAll<HTMLElement>(".calendar-drag-target-day")) {
          if (!toTag.has(prev)) prev.classList.remove("calendar-drag-target-day");
        }
        for (const cellEl of toTag) cellEl.classList.add("calendar-drag-target-day");
      };
      document.addEventListener("pointermove", handler as EventListener);
      // Some FC pointer-capture paths swallow pointermove redispatch;
      // mousemove keeps firing in those cases. Listen to both for redundancy.
      document.addEventListener("mousemove", handler as EventListener);
      moveHandler = handler;
    },
    endDrag: releaseSessionState,
    removeGhost,
    dispose: () => {
      releaseSessionState();
      removeGhost();
    },
    getActiveEl: () => activeEl,
  };
}

/**
 * The drag-to-reschedule orchestrator. Owns:
 *  - eventAllow predicate (permission gate at drag start),
 *  - drag lifecycle (Esc to cancel, body class for cursor, ghost footprint),
 *  - drop branching (read-only / recurring / has-guests / solo),
 *  - the optimistic PATCH + revert,
 *  - the Notion-style confirm dialog + scope picker + undo toast.
 */
export function useEventDrag({ enabled, calendarsById }: UseEventDragOptions): UseEventDragResult {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const canvas = useCanvasActionContext();

  /**
   * Open an event's detail surface: the canvas card where a canvas is mounted,
   * and the event's own page everywhere else (mobile, gate-off). Paired with
   * {@link setEventProposal}, which carries the dragged-to times to whichever
   * surface this resolves to.
   */
  const openEventDetail = useCallback(
    (eventId: string) => {
      if (canvas?.resetTo(toUrnString({ type: "source", id: eventId }))) return;
      setLocation(eventDetailPath(eventId));
    },
    [canvas, setLocation],
  );
  const setPendingThreadMessages = useSetAtom(pendingThreadMessagesAtom);
  const { dialog: recurringScopeDialog, askForScope } = useRecurringScopePromise();
  const { dialog: confirmDialog, askForConfirm } = useRescheduleConfirmPromise();
  const { getUserTimezone, getUserLocation } = useCurrentUserContext();

  // Hand off the rescheduled event to a new agent thread, pre-loaded with
  // the move context so the agent can draft a note / propose alternates
  // without the user re-typing the event details. Mirrors the synthesized
  // first-message format from spec §4 ("I moved 'Standup' from … to …,
  // notified N guests — <user's prompt>").
  const handoffToAgentThread = useCallback(
    async (args: {
      title: string;
      oldStart: Date;
      oldEnd: Date;
      newStart: Date;
      newEnd: Date;
      isAllDay: boolean;
      guestCount: number;
      notified: boolean;
      prompt: string;
    }) => {
      try {
        const { id: threadId } = await createNewTopicThread({ title: "", artifactIds: [] });
        if (!threadId) {
          errorToast("Couldn't start the agent thread");
          return;
        }
        const formatMove = (d: Date) =>
          args.isAllDay ? format(d, "EEE MMM d") : format(d, "EEE MMM d, h:mm a");
        const agentContent = [
          `I moved '${args.title}' from ${formatMove(args.oldStart)} to ${formatMove(args.newStart)}`,
          args.notified && args.guestCount > 0
            ? ` and notified ${args.guestCount} guest${args.guestCount === 1 ? "" : "s"}`
            : "",
          ".",
        ].join("");
        const userLocation = getUserLocation();
        const body: NewMessageReq = {
          text: args.prompt,
          type: "userRequest",
          agentContent,
          clientContext: {
            userTimezone: getUserTimezone(),
            userLocation: userLocation || undefined,
          },
        };
        const { data } = await api.post<NewMessageResp>(
          `/api/a/threads/${threadId}/messages`,
          body,
        );
        // Seed an optimistic message keyed on the real event id so the
        // newly-opened thread renders the message in the feed immediately
        // — without this the message hangs in the queued pill until the
        // actor picks it up. Mirrors the home Omnibox's handoff.
        if (data.eventId !== undefined) {
          const { item: optimisticMessage } = buildOptimisticUserMessage(threadId, body, {
            status: "completed",
            suppressQueuedPill: true,
            id: data.eventId,
          });
          setPendingThreadMessages((prev) => ({ ...prev, [threadId]: optimisticMessage }));
        }
        updateThreadOptimistically(queryClient, threadId, { activityState: "running" });
        // The drag-spawned thread opens on its own page.
        setLocation(getThreadPagePath(threadId));
      } catch (err) {
        console.error("[useEventDrag] handoffToAgentThread failed", err);
        // The upgrade dialog already explains a refusal.
        if (!parseUnitsExhaustedError(err)) errorToast("Couldn't open the agent thread");
      }
    },
    [getUserTimezone, getUserLocation, setLocation, queryClient, setPendingThreadMessages],
  );

  // Single owner of every side-effecting DOM piece — ghost clone, pointermove
  // listener, body class, day-cell tags, active-element ref. Built once and
  // re-used across the hook's lifetime; abandon paths (Esc, unmount) call
  // `dispose()` and normal mouse-up calls `endDrag()`. See the controller's
  // JSDoc for why this is centralized.
  const controllerRef = useRef<DragVisualController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = createDragVisualController();
  }
  const controller = controllerRef.current;

  // Tracks whether the hook's owner component is still mounted. The async
  // `eventDrop` body awaits user dialogs and a PATCH; if the calendar
  // unmounts mid-await, post-await code shouldn't run.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Esc handler installed on document during the lifetime of the hook —
  // covers FC's gap where it does not natively support Escape-to-cancel.
  // Dispatches a synthetic `pointercancel` on the dragged chip; FC's
  // interaction plugin treats this as a drag abort and the source chip stays
  // at its original position without firing `eventDrop`. The bubble flag is
  // critical: FC listens at the document level.
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const el = controller.getActiveEl();
      if (!el) return;
      el.dispatchEvent(new PointerEvent("pointercancel", { bubbles: true }));
      controller.dispose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [enabled, controller]);

  // Safety-net cleanup on unmount — full teardown via `dispose()` covers
  // both the body class (otherwise `cursor: grabbing !important` would
  // stick) and any in-flight listener / ghost / day-cell tags.
  useEffect(() => {
    return () => {
      controller.dispose();
    };
  }, [controller]);

  const updateMutation = useMutation({ mutationFn: updateEvent });

  const eventAllow = useCallback(
    (_dropInfo: { start: Date; end: Date; allDay: boolean }, draggedEvent: EventApi | null) => {
      if (!enabled) return false;
      if (!draggedEvent) return false;
      return canDragEvent(fcEventToMinimalCalendarEvent(draggedEvent), calendarsById);
    },
    [enabled, calendarsById],
  );

  const eventDragStart = useCallback(
    (info: { el: HTMLElement; event: EventApi }) => {
      if (!enabled) return;
      controller.start(info.el ?? null);
    },
    [enabled, controller],
  );

  // Mouse-up reached: release session-scoped state (listener / body class /
  // day-cell tags) but DON'T touch the ghost. The ghost must persist as a
  // "where it came from" reference until the drop flow terminally resolves
  // (Send / Discard / Continue editing / undo toast). Each terminal branch
  // of `eventDrop` calls `controller.removeGhost()`.
  const eventDragStop = useCallback(() => {
    controller.endDrag();
  }, [controller]);

  const removeGhost = useCallback(() => {
    controller.removeGhost();
  }, [controller]);

  const performPatch = useCallback(
    async (args: {
      info: EventDropArg;
      snapshot: ReturnType<typeof snapshotEvent>;
      editScope: RecurringEditScope;
      sendUpdates?: "all" | "none";
      showUndo: boolean;
      originalStart: Date;
      originalEnd: Date;
      originalIsAllDay: boolean;
    }) => {
      const {
        info,
        snapshot,
        editScope,
        sendUpdates,
        showUndo,
        originalStart,
        originalEnd,
        originalIsAllDay,
      } = args;
      const { sourceEntId, calendarId, externalEventId, start, end } = snapshot;
      // A reminder is connectorless and has no provider id — the source ent id
      // is its whole identity. Requiring one here would fail the drop after the
      // chip had already moved, which reads worse than never dragging at all.
      if (!sourceEntId || !calendarId || (!externalEventId && !snapshot.isReminder)) {
        info.revert();
        removeGhost();
        errorToast("Couldn't move event");
        return;
      }

      const isAllDayRelevant = snapshot.isAllDay || originalIsAllDay;

      // Move this meeting's travel buffers in the same frame. They re-anchor on
      // the backend asynchronously (the apply_event_change cascade), so without
      // this they'd lag a beat behind the dragged meeting. The overlay keeps
      // them put through the reconciling refetch until the cascade catches up.
      const bufferExternalEventIds = bufferIdsOf(info.event);
      const deltaMs = start.getTime() - originalStart.getTime();
      const bufferMoves = shiftBufferEvents(info, bufferExternalEventIds, deltaMs);
      if (bufferMoves.length > 0) addPendingBufferMoves(bufferMoves);

      try {
        await updateMutation.mutateAsync({
          sourceEntId,
          calendarId,
          externalEventId: externalEventId ?? undefined,
          ...buildMoveDates(start, end, snapshot.isAllDay, isAllDayRelevant, snapshot.isReminder),
          editScope,
          sendUpdates,
        });
        // Invalidate calendar queries so the optimistic FC move is reconciled
        // with the server response once the sync socket reports the new state.
        void queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
        void queryClient.invalidateQueries({ queryKey: ["event", sourceEntId] });
        // A source's instanceKey embeds its start time, so moving an event
        // changes it. Without this the spotlight keeps matching the pre-move key
        // and silently stops ringing the chip.
        void queryClient.invalidateQueries({ queryKey: ["source"] });
        removeGhost();

        if (showUndo) {
          const movedLabel = snapshot.isAllDay
            ? format(start, "EEE MMM d")
            : format(start, "EEE MMM d, h:mm a");
          confirmationToast(
            `Moved to ${movedLabel}`,
            { duration: UNDO_TOAST_DURATION_MS },
            {
              actionText: "Undo",
              onAction: async (toastId) => {
                sonnerToast.dismiss(toastId);
                // Move the buffers back with the meeting (reverse delta).
                const revertedMoves = shiftBufferEvents(info, bufferExternalEventIds, -deltaMs);
                if (revertedMoves.length > 0) addPendingBufferMoves(revertedMoves);
                try {
                  await updateMutation.mutateAsync({
                    sourceEntId,
                    calendarId,
                    externalEventId: externalEventId ?? undefined,
                    // Undo must restore the original all-day form, not the
                    // post-drag one — reverting an all-day→timed conversion
                    // means converting back.
                    ...buildMoveDates(
                      originalStart,
                      originalEnd,
                      originalIsAllDay,
                      isAllDayRelevant,
                      snapshot.isReminder,
                    ),
                    editScope,
                    sendUpdates,
                  });
                  void queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
                  void queryClient.invalidateQueries({ queryKey: ["event", sourceEntId] });
                  void queryClient.invalidateQueries({ queryKey: ["source"] });
                } catch (err) {
                  errorToast(errorMessageFor(err));
                  void queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
                  void queryClient.invalidateQueries({ queryKey: ["event", sourceEntId] });
                  void queryClient.invalidateQueries({ queryKey: ["source"] });
                }
              },
            },
          );
        }
      } catch (err) {
        // Move the buffers back and drop their optimistic overlay entries so the
        // failed drag reverts as a unit with the meeting.
        if (bufferMoves.length > 0) {
          shiftBufferEvents(info, bufferExternalEventIds, -deltaMs);
          removePendingBufferMoves(bufferExternalEventIds);
        }
        info.revert();
        removeGhost();
        errorToast(errorMessageFor(err));
      }
    },
    [updateMutation, queryClient, removeGhost],
  );

  const eventDrop = useCallback(
    async (info: EventDropArg) => {
      if (!enabled) {
        info.revert();
        return;
      }
      let reverted = false;
      const safeRevert = () => {
        if (!reverted) {
          reverted = true;
          info.revert();
        }
      };
      try {
        // Capture original times BEFORE we touch anything — FC has already
        // mutated event.start/end to the new slot. info.oldEvent preserves
        // the pre-drag snapshot. For single-day all-day events FC sets `end`
        // to null (exclusive-end convention), so we derive a proper end via
        // `addDays(start, 1)` rather than collapsing to `start` — otherwise
        // Undo would PATCH a zero-duration event.
        const originalStart = info.oldEvent.start ?? new Date();
        const originalEnd =
          info.oldEvent.end ?? (info.oldEvent.allDay ? addDays(originalStart, 1) : originalStart);
        const originalIsAllDay = info.oldEvent.allDay;

        const snapshot = snapshotEvent(info.event);

        // Defense-in-depth: re-check the predicate at drop time. eventAllow
        // already gated drag start, but a socket invalidation mid-drag could
        // have flipped writability.
        if (!canDragEvent(fcEventToMinimalCalendarEvent(info.event), calendarsById)) {
          safeRevert();
          removeGhost();
          errorToast("Can't reschedule this event");
          return;
        }

        // Recurring scope first. Non-recurring events skip the picker.
        let editScope: RecurringEditScope = "thisEvent";
        // Treat any event with a recurring parent ID as recurring even if
        // the boolean flag is missing (defensive against inconsistent data).
        if (snapshot.isRecurring || snapshot.recurringEventId) {
          const choice = await askForScope();
          if (!mountedRef.current) return;
          if (!choice) {
            safeRevert();
            removeGhost();
            return;
          }
          editScope = choice;
        }

        // A reminder's participants are recipients, not guests. Nobody is
        // invited to a reminder and nobody replies to one, so moving it sends no
        // update and must never raise the notify-guests dialog — it just goes
        // out at the new time.
        const hasGuests =
          !snapshot.isReminder && (snapshot.participants ?? []).some((p) => !p.self);

        if (!hasGuests) {
          // Silent save + undo toast.
          await performPatch({
            info,
            snapshot,
            editScope,
            showUndo: true,
            originalStart,
            originalEnd,
            originalIsAllDay,
          });
          return;
        }

        // Has guests: ask the Notion-style confirm dialog.
        const calendar = calendarsById?.get(snapshot.calendarId ?? "");
        const choice = await askForConfirm({
          oldStart: originalStart,
          oldEnd: originalEnd,
          newStart: snapshot.start,
          newEnd: snapshot.end,
          isAllDay: snapshot.isAllDay,
          allowSendWithoutEmail: supportsSendWithoutEmail(calendar),
        });
        if (!mountedRef.current) return;

        switch (choice.kind) {
          case "discard":
            safeRevert();
            removeGhost();
            return;
          case "continueEditing": {
            // Revert the FC visual move (the detail surface owns the new times
            // until the user saves), hand the proposed times over out of band,
            // then open the event where the user can commit them.
            safeRevert();
            removeGhost();
            const eventId = snapshot.sourceEntId;
            if (eventId) {
              setEventProposal({
                eventId,
                startIso: snapshot.start.toISOString(),
                endIso: snapshot.end.toISOString(),
              });
              openEventDetail(eventId);
            }
            return;
          }
          case "sendUpdate":
            await performPatch({
              info,
              snapshot,
              editScope,
              sendUpdates: choice.notify ? "all" : "none",
              showUndo: false, // Guest path already had an explicit confirmation step
              originalStart,
              originalEnd,
              originalIsAllDay,
            });
            return;
          case "sendUpdateAndAsk": {
            // Same as sendUpdate, honoring the dialog's notify checkbox, then
            // hand off to the agent with the user's prompt + the move context.
            // The patch fires first so the user sees the visible move land
            // before the navigation; if it fails, we skip the agent handoff.
            await performPatch({
              info,
              snapshot,
              editScope,
              sendUpdates: choice.notify ? "all" : "none",
              showUndo: false,
              originalStart,
              originalEnd,
              originalIsAllDay,
            });
            if (!mountedRef.current) return;
            const guestCount = (snapshot.participants ?? []).filter((p) => !p.self).length;
            await handoffToAgentThread({
              title: snapshot.title || "Event",
              oldStart: originalStart,
              oldEnd: originalEnd,
              newStart: snapshot.start,
              newEnd: snapshot.end,
              isAllDay: snapshot.isAllDay,
              guestCount,
              notified: choice.notify,
              prompt: choice.prompt,
            });
            return;
          }
        }
      } catch (err) {
        // Safety net — if any await above rejects (dialog error, mutation
        // crash) we still revert FC's optimistic move and clean up the
        // ghost so the user isn't left with a half-applied drag state.
        console.error("[useEventDrag] unexpected error in eventDrop", err);
        safeRevert();
        removeGhost();
        errorToast(errorMessageFor(err));
      }
    },
    [
      enabled,
      calendarsById,
      askForScope,
      askForConfirm,
      openEventDetail,
      performPatch,
      removeGhost,
      handoffToAgentThread,
    ],
  );

  const fcProps = useMemo(
    () => ({
      editable: enabled,
      eventStartEditable: enabled,
      eventDurationEditable: false, // Resize-by-edge out of scope for v1
      eventAllow,
      eventDragStart,
      eventDragStop,
      eventDrop,
    }),
    [enabled, eventAllow, eventDragStart, eventDragStop, eventDrop],
  );

  const dragElements = useMemo(
    () => (
      <>
        {recurringScopeDialog}
        {confirmDialog}
      </>
    ),
    [recurringScopeDialog, confirmDialog],
  );

  return { fcProps, dragElements };
}
